<template>
  <div class="vuestic-data-table">
    <div class="d-flex flex-md-row flex-column align-items-center d-print-none"
         :class="controlsAlignmentClass"
         v-if="hasHeader">
      <filter-bar
        v-model="filterText"
        :label="filterInputLabel"
        v-if="filterInputShown"
        :debounce="500"
      />
      <div class="datatable-controls d-flex flex-row-reverse flex-wrap">
        <items-per-page :options="itemsPerPage"
                        :label="itemsPerPageLabel"
                        :defaultPerPage="defaultPerPageComputed"
                        @items-per-page="onItemsPerPage"
                        v-if="perPageSelectorShown"></items-per-page>
        <slot name="additionalTableControl"></slot>
      </div>
    </div>
    <vuetable
      ref="vuetable"
      :apiUrl="apiUrl"
      :apiMode="true"
      :httpFetch="httpFetch"
      :httpOptions="httpOptions"
      :fields="displayTableFields"
      :css="css.table"
      dataPath="docs"
      paginationPath="pagination"
      :appendParams="moreParams"
      :perPage="perPage"
      :queryParams="{sort: 'sort', page: 'page', perPage: 'limit'}"
      :detail-row-component="detailRowComponent"
      :sortOrder="sortOrder"
      :class="{'row-clickable': rowClickable, 'row-selectable': rowSelectable}"
      trackBy="_id"
      :rowClass="row => $refs.vuetable.rowSelected(row) ? 'selected' : ''"
      @vuetable:pagination-data="onPaginationData"
      @vuetable:loading="fireEvent('vuetable:loading', arguments)"
      @vuetable:loaded="fireEvent('vuetable:loaded', arguments)"
      @vuetable:load-success="fireEvent('vuetable:load-success', arguments)"
      @vuetable:load-error="fireEvent('vuetable:load-error', arguments)"
      @vuetable:row-clicked="fireEvent('vuetable:row-clicked', arguments)"
      @vuetable:row-dblclicked="fireEvent('vuetable:row-dblclicked', arguments)"
      @vuetable:detail-row-clicked="fireEvent('vuetable:detail-row-clicked', arguments)"
      @vuetable:cell-clicked="fireEvent('vuetable:cell-clicked', arguments)"
      @vuetable:cell-dblclicked="fireEvent('vuetable:cell-dblclicked', arguments)"
      @vuetable:cell-rightclicked="fireEvent('vuetable:cell-rightclicked', arguments)"
    >
      <template v-for="f in displayTableFields"
                v-if="f.name.substr(0, 7) === '__slot:'"
                :slot="f.name.substr(7)"
                slot-scope="s">
        <slot :name="f.name.substr(7)" :rowIndex="s.rowIndex" :rowData="s.rowData"></slot>
      </template>
    </vuetable>
    <div class="d-flex justify-content-center mb-4 d-print-none">
      <vuetable-pagination ref="pagination"
                           :css="css.pagination"
                           :onEachSide="onEachSide"
                           @vuetable-pagination:change-page="onChangePage">
      </vuetable-pagination>
    </div>
  </div>
</template>

<script>
  import Vuetable from 'vuetable-2/src/components/Vuetable';
  import VuetablePagination from 'vuetable-2/src/components/VuetablePagination';
  import FilterBar from './datatable-components/FilterBar.vue';
  import ItemsPerPage from './datatable-components/ItemsPerPage.vue';
  import DefaultPerPageDefinition from './data/items-per-page-definition';
  import Vue from 'vue';
  import DataTableStyles from '../vuestic-datatable/data/data-table-styles';
  import { searchParams } from '@/services/schemaUtils';

  export default {
    name: 'custom-data-table',
    components: {
      FilterBar,
      Vuetable,
      VuetablePagination,
      ItemsPerPage,
    },
    props: {
      apiUrl: {
        type: String,
      },
      // httpFetch: {
      //   type: Function,
      //   default: null
      // },
      httpOptions: {
        type: Object,
        default: () => {
        },
      },
      // filterQuery: {
      //   type: String,
      //   default: 'filter'
      // },
      tableFields: {
        type: Array,
        required: true,
      },
      itemsPerPage: {
        type: Array,
        default: () => DefaultPerPageDefinition.itemsPerPage,
      },
      perPageSelectorShown: {
        type: Boolean,
        default: true,
      },
      filterInputShown: {
        type: Boolean,
        default: true,
      },
      filterInputLabel: {
        type: String,
        default () {
          return this.$t('Search');
        },
      },
      itemsPerPageLabel: {
        type: String,
        default () {
          return this.$t('/ page');
        },
      },
      defaultPerPage: {
        type: Number,
        default: DefaultPerPageDefinition.itemsPerPage[0].value,
      },
      onEachSide: {
        type: Number,
        default: 2,
      },
      rowClickable: {
        type: Boolean,
        default: true,
      },
      rowSelectable: {
        type: Boolean,
        default: false,
      },
      // apiMode: {
      //   type: Boolean,
      //   default: true
      // },
      // tableData: {
      //   type: Object,
      //   default () {
      //     return {
      //       data: []
      //     }
      //   }
      // },
      // dataModeFilterableFields: {
      //   type: Array,
      //   default: () => [],
      // },
      // sortFunctions: {
      //   type: Object,
      // },
      sortOrder: {
        type: Array,
        default: () => [{field: 'id', sortField: '_id', direction: 'desc'}],
      },
      // paginationPath: {
      //   type: String,
      //   default: '',
      // },
      appendParams: {
        type: Object,
      },
      detailRowComponent: {},
      useOldFilter: {type: Boolean, default: true},
    },
    data () {
      return {
        perPage: 0,
        colorClasses: {},
        filterText: '',
        css: DataTableStyles,
      };
    },
    computed: {
      hasHeader () {
        return this.filterInputShown || this.perPageSelectorShown || this.$slots.additionalTableControl;
      },
      vuetable () {
        return this.$refs.vuetable;
      },
      controlsAlignmentClass () {
        return {
          'justify-content-md-between': this.filterInputShown,
          'justify-content-md-end': !this.filterInputShown,
        };
      },
      displayTableFields () {
        return this.tableFields.filter(x => !x.hide);
      },
      moreParams () {
        let out;
        if (this.useOldFilter) {
          if (this.appendParams && this.appendParams.filter) {
            out = Object.assign({}, this.appendParams, {
              filter: {$and: [this.filterParams, this.appendParams.filter].filter(x => x)},
            });
          } else {
            out = Object.assign({filter: this.filterParams}, this.appendParams);
          }
        } else {
          if (this.appendParams && this.appendParams.search) {
            out = Object.assign({}, this.appendParams, {
              search: [this.filterText].concat(this.appendParams.search),
            });
          } else {
            out = Object.assign({}, this.appendParams, {search: this.filterText});
          }
        }
        return out;
      },
      filterParams () {
        return searchParams(this.tableFields, this.filterText);
      },
      defaultPerPageComputed () {
        let defaultPerPage = DefaultPerPageDefinition.itemsPerPage[1].value;

        if (this.$options.propsData.defaultPerPage) {
          defaultPerPage = this.$options.propsData.defaultPerPage;
        } else if (this.$options.propsData.itemsPerPage) {
          defaultPerPage = this.$options.propsData.itemsPerPage[0].value;
        }

        return defaultPerPage;
      },
      // paginationPathComputed () {
      //   return this.apiMode ? this.paginationPath : 'pagination';
      // },
      selectedIds: {
        get () {
          return this.$refs.vuetable && this.$refs.vuetable.selectedTo;
        },
        set (v) {
          this.$refs.vuetable && (this.$refs.vuetable.selectedTo = v);
        },
      },
      selectedRows () {
        if (!this.$refs.vuetable) return [];
        return this.$refs.vuetable.selectedTo.map(x => this.$refs.vuetable.tableData.find(y => y._id === x));
      },
    },
    watch: {
      moreParams () {
        Vue.nextTick(() => this.$refs.vuetable.refresh());
      },
      perPage () {
        Vue.nextTick(() => this.$refs.vuetable.refresh());
      },
      tableFields () {
        Vue.nextTick(() => this.$refs.vuetable.normalizeFields());
      },
    },

    created () {
      this.perPage = this.defaultPerPageComputed;
    },

    mounted () {
      this.$emit('initialized', this.$refs.vuetable);
    },

    methods: {
      httpFetch (url, options) {
        if (options.params) {
          for (let key of ['filter', 'populate', 'sort', 'select', 'search']) {
            if (options.params[key]) {
              options.params[key] = window.RISON.encode(options.params[key]);
            }
          }
        }
        return this.$http.get(url, options);
      },
      getSortParam (sort) {
        return sort.reduce((acc, v) => {
          acc[v.sortField] = v.direction === 'asc' ? 1 : -1;
          return acc;
        }, {});
      },
      transform (data) {
        data.pagination = {
          'total': data.total,
          'per_page': this.perPage,
          'current_page': data.page,
          'last_page': data.pages || 0,
          'from': (data.page - 1) * this.perPage + 1,
          'to': Math.min(data.page * this.perPage, data.total),
        };
        return data;
      },
      onFilterSet (filterText) {
        this.filterText = filterText;
      },
      onItemsPerPage (itemsPerPageValue) {
        this.perPage = itemsPerPageValue;
      },
      onPaginationData (paginationData) {
        this.$refs.pagination.setPaginationData(paginationData);
      },
      onChangePage (page) {
        this.$refs.vuetable.changePage(page);
      },
      refresh () {
        this.$refs.vuetable && this.$refs.vuetable.refresh();
      },
      reload () {
        this.$refs.vuetable && this.$refs.vuetable.reload();
      },
      fireEvent (name, args) {
        return this.$emit(name, ...args);
      },
      loadPage (n) {
        this.$refs.pagination && this.$refs.pagination.loadPage(n);
      },
    },
  };
</script>

<style lang="scss">
  @import "../../../sass/variables";

  .vuestic-data-table {
    .form-group {
      margin-bottom : 1rem;
    }

    .row-clickable tr {
      cursor : pointer;

      &:hover {
        outline : 2px solid $brand-primary;
      }
    }

    .row-selectable tr.selected {
      background-color : $brand-primary !important;
      color            : $white !important;
    }

    @media (max-width : 1258px) {
      .pagination-link-btn:first-child, .pagination-link-btn:last-child {
        display : none;
      }

      .pagination-link-btn:nth-child(2) {
        border-top-left-radius    : $btn-border-radius !important;
        border-bottom-left-radius : $btn-border-radius !important;
      }

      .pagination-link-btn:nth-last-child(2) {
        border-top-right-radius    : $btn-border-radius !important;
        border-bottom-right-radius : $btn-border-radius !important;
      }
    }

    @media (max-width : 576px) {
      .hide-not-focused-btn:not(.focus) {
        display : none;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/vuestic-theme/vuestic-components/vuestic-datatable/CustomDataTable.vue